import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'

import {
  EmployeeJobCatalogRolesFilterDocument,
  EmployeeJobCatalogTreeNodesFilterDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { type SerializedTreeNode } from 'modules/job_catalog/components/JobCatalogTree/types'

type Params = {
  hasJobCatalogTreeFeature: boolean
}

const flattenTreeToOptions = (
  nodes: SerializedTreeNode[]
): Array<{ label: string; value: string }> => {
  const options: Array<{ label: string; value: string }> = []

  const findRoles = (node: SerializedTreeNode): void => {
    if (node.type === 'jobcatalog_treerole' && node.children) {
      node.children.forEach((child) => {
        if (child.type === 'jobcatalog_treelevel' && child.name) {
          const combinedLabel = `${node.name} / ${child.name}`
          options.push({
            label: combinedLabel,
            value: child.node_uuid,
          })
        }
      })

      return
    }

    if (node.children) {
      node.children.forEach((child) => findRoles(child))
    }
  }

  nodes.forEach((node) => findRoles(node))

  return options
}

const getTreeOptions = (serializedTree?: string | null) => {
  const parsedTree = serializedTree ? JSON.parse(serializedTree) : []
  const tree: SerializedTreeNode[] = Array.isArray(parsedTree) ? parsedTree : [parsedTree]

  return flattenTreeToOptions(tree)
}

const getRoleOptions = (rolesData?: {
  jobCatalog: {
    roles: Array<{
      name: string
      levels: Array<{ id: string | number; isDefault: boolean; name: string }>
    }>
  }
}) =>
  rolesData?.jobCatalog.roles.flatMap((role) =>
    role.levels.map((level) => ({
      label: level.isDefault ? role.name : `${role.name} / ${level.name}`,
      value: String(level.id),
    }))
  ) ?? []

export const useJobCatalogRoleOptions = ({ hasJobCatalogTreeFeature }: Params) => {
  const { data: treeData } = useQuery(EmployeeJobCatalogTreeNodesFilterDocument, {
    skip: !hasJobCatalogTreeFeature,
  })

  const { data: rolesData } = useQuery(EmployeeJobCatalogRolesFilterDocument, {
    skip: hasJobCatalogTreeFeature,
  })

  return useMemo(() => {
    if (hasJobCatalogTreeFeature) {
      return getTreeOptions(treeData?.jobCatalog.treesConnection.nodes[0]?.serializedTree)
    }

    return getRoleOptions(rolesData)
  }, [hasJobCatalogTreeFeature, rolesData, treeData])
}

export const useJobCatalogRoleFilter = ({ hasJobCatalogTreeFeature }: Params) => {
  const client = useApolloClient()

  return useMemo(
    () => ({
      type: 'in' as const,
      label: i18n.t('employees.employees_list.filters.job_role'),
      options: {
        options: async () => {
          if (hasJobCatalogTreeFeature) {
            const { data } = await client.query({
              query: EmployeeJobCatalogTreeNodesFilterDocument,
              fetchPolicy: 'cache-first',
            })

            return getTreeOptions(data.jobCatalog.treesConnection.nodes[0]?.serializedTree)
          }

          const { data } = await client.query({
            query: EmployeeJobCatalogRolesFilterDocument,
            fetchPolicy: 'cache-first',
          })

          return getRoleOptions(data)
        },
      },
    }),
    [client, hasJobCatalogTreeFeature]
  )
}
